import React, { Suspense } from "react";
import { loadDocViewer } from "@/extension/utils/lazy-lib";

/**
 * React.lazy + Suspense boundary over @cyntler/react-doc-viewer's <DocViewer/>.
 *
 * DocViewer pulls in react-pdf (pdfjs), styled-components, papaparse, mustache and
 * ajv (~228KB) but only renders inside the task media-manager dialog, which opens
 * behind a user interaction. This boundary loads the picker on demand from the
 * lazy/doc-viewer.js WAR bundle (which shares app.js's React via the __BT_REACT__
 * bridge — see loadDocViewer in src/extension/utils/lazy-lib.ts).
 *
 * The lazy bundle's entry (doc-viewer-lazy.js) OWNS the renderer composition (the
 * built-in DocViewerRenderers + the custom webp renderer) and exports a component
 * that already bakes in `pluginRenderers`, so callers here just pass documents /
 * config / theme / className — no library value is imported into app.js.
 */

// Loaded once per document; React.lazy memoizes the promise internally.
const DocViewerLazy = React.lazy(async () => {
  const Comp = await loadDocViewer();
  return { default: Comp };
});

export type LazyDocViewerProps = {
  documents: { uri: string; fileName?: string }[];
  config?: unknown;
  theme?: unknown;
  className?: string;
};

export default function LazyDocViewer(props: LazyDocViewerProps) {
  return (
    <Suspense
      fallback={
        <div
          aria-hidden="true"
          style={{ width: "100%", height: "100%", minHeight: 300 }}
        />
      }
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <DocViewerLazy {...(props as any)} />
    </Suspense>
  );
}
